import { createGetAPI, createPostAPI, createPatchAPI } from '@cuemath/rest-api';
import { CHATBOT_BASE_URL } from 'constants/api';

// --- Dispositions ---

const {
  useGet: useGetDispositionTree,
  invalidate: invalidateDispositionTree,
} = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/dispositions/tree/`,
});

// Legacy — path-based disposition detail (kept until all screens migrate to id-based).
const { useGet: useGetDispositionDetail } = createGetAPI({
  getURL: path => `${CHATBOT_BASE_URL}/dispositions/${path}/?use_draft=true`,
});

// Legacy — path-based PATCH (pre-versioning UI relies on this for save/delete).
const { usePatch: usePatchDisposition } = createPatchAPI({
  getURL: path => `${CHATBOT_BASE_URL}/dispositions/${path}/`,
});

// Legacy — dedicated publish endpoint. New flow uses PATCH with status:'PUBLISHED'.
const { usePost: usePublishDisposition } = createPostAPI({
  getURL: (_, path) => `${CHATBOT_BASE_URL}/dispositions/${path}/publish/`,
});

// GET /dispositions/<uuid:id>/ — fetch a specific version by id.
const { useGet: useGetDispositionVersion } = createGetAPI({
  getURL: id => `${CHATBOT_BASE_URL}/dispositions/${id}/`,
});

// GET /dispositions/versions/?path=X — all versions (DRAFT/PUBLISHED/ARCHIVED) for a path.
const {
  useGet: useGetDispositionVersions,
  invalidate: invalidateDispositionVersions,
} = createGetAPI({
  getURL: path =>
    `${CHATBOT_BASE_URL}/dispositions/versions/?path=${encodeURIComponent(path || '')}`,
});

// POST /dispositions/ — body is either {path, ...fields} (new v1 draft) or {source_id, ...overrides} (copy-draft).
const { usePost: usePostDisposition } = createPostAPI({
  getURL: () => `${CHATBOT_BASE_URL}/dispositions/`,
});

// PATCH /dispositions/<uuid:id>/ — body drives state:
// {status: 'PUBLISHED'} publishes; {status: 'ARCHIVED'} unpublishes;
// {is_deleted: true} soft-deletes; otherwise edits fields (DRAFT only).
const { usePatch: usePatchDispositionVersion } = createPatchAPI({
  getURL: id => `${CHATBOT_BASE_URL}/dispositions/${id}/`,
});

// --- Disposition Children ---

const { useGet: useGetDispositionChildren } = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/dispositions/children/`,
});

// --- Categories ---

const { useGet: useGetCategories } = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/categories/`,
});

const { usePatch: usePatchCategory } = createPatchAPI({
  getURL: path => `${CHATBOT_BASE_URL}/categories/${path}/`,
});

// --- KB Articles ---

const { useGet: useGetKBArticles, invalidate: invalidateKBArticles } = createGetAPI({
  getURL: botConfig => `${CHATBOT_BASE_URL}/kb-articles/?bot_config=${botConfig}`,
});

const { useGet: useGetKBArticleDetail } = createGetAPI({
  getURL: id => `${CHATBOT_BASE_URL}/kb-articles/${id}/`,
});

const { usePost: usePostKBArticle } = createPostAPI({
  getURL: () => `${CHATBOT_BASE_URL}/kb-articles/`,
});

// PATCH /kb-articles/<uuid:id>/ — body drives state transitions (mirrors disposition PATCH).
const { usePatch: usePatchKBArticle } = createPatchAPI({
  getURL: id => `${CHATBOT_BASE_URL}/kb-articles/${id}/`,
});

// Legacy — dedicated publish endpoint. New flow uses PATCH with status:'PUBLISHED'.
const { usePost: usePublishKBArticle } = createPostAPI({
  getURL: (_, id) => `${CHATBOT_BASE_URL}/kb-articles/${id}/publish/`,
});

// GET /kb-articles/versions/?article_key=X — full version history for an article.
const {
  useGet: useGetKBArticleVersions,
  invalidate: invalidateKBArticleVersions,
} = createGetAPI({
  getURL: articleKey =>
    `${CHATBOT_BASE_URL}/kb-articles/versions/?article_key=${encodeURIComponent(
      articleKey || '',
    )}`,
});

// --- Bots ---

const { useGet: useGetBots, invalidate: invalidateBots } = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/bots/`,
});

const { useGet: useGetBotDetail } = createGetAPI({
  getURL: id => `${CHATBOT_BASE_URL}/bots/${id}/`,
});

const { usePost: usePostBot } = createPostAPI({
  getURL: () => `${CHATBOT_BASE_URL}/bots/`,
});

// PATCH /bots/<uuid:id>/ — body drives state transitions (mirrors disposition PATCH).
const { usePatch: usePatchBot } = createPatchAPI({
  getURL: id => `${CHATBOT_BASE_URL}/bots/${id}/`,
});

// GET /bots/versions/?bot_key=X — full version history for a bot.
const { useGet: useGetBotVersions, invalidate: invalidateBotVersions } = createGetAPI({
  getURL: botKey =>
    `${CHATBOT_BASE_URL}/bots/versions/?bot_key=${encodeURIComponent(botKey || '')}`,
});

// --- Channels ---

const { useGet: useGetChannels, invalidate: invalidateChannels } = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/channels/`,
});

const { useGet: useGetChannelDetail } = createGetAPI({
  getURL: id => `${CHATBOT_BASE_URL}/channels/${id}/`,
});

const { usePost: usePostChannel } = createPostAPI({
  getURL: () => `${CHATBOT_BASE_URL}/channels/`,
});

const { usePatch: usePatchChannel } = createPatchAPI({
  getURL: id => `${CHATBOT_BASE_URL}/channels/${id}/`,
});

// --- Escalation Teams ---

const { useGet: useGetEscalationTeams } = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/escalation-teams/`,
});

// --- Disposition Properties (catalog of customer-property flags) ---

const { useGet: useGetDispositionProperties } = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/disposition-properties/`,
});

// --- Tools ---

const { useGet: useGetTools } = createGetAPI({
  getURL: _id => `${CHATBOT_BASE_URL}/tools/`,
});

const { usePost: usePostToolTest } = createPostAPI({
  getURL: (_, name) => `${CHATBOT_BASE_URL}/tools/${name}/test/`,
});

// --- Test Chat ---

const { usePost: usePostTestChatMessage } = createPostAPI({
  getURL: () => `${CHATBOT_BASE_URL}/test-chat/message/`,
});

const { usePost: usePostTicket } = createPostAPI({
  getURL: () => `${CHATBOT_BASE_URL}/tickets/`,
});

export {
  // Dispositions
  useGetDispositionTree,
  invalidateDispositionTree,
  useGetDispositionDetail,
  useGetDispositionVersion,
  useGetDispositionVersions,
  invalidateDispositionVersions,
  usePostDisposition,
  usePatchDisposition,
  usePatchDispositionVersion,
  usePublishDisposition,
  // Disposition Children
  useGetDispositionChildren,
  // Categories
  useGetCategories,
  usePatchCategory,
  // KB Articles
  useGetKBArticles,
  invalidateKBArticles,
  useGetKBArticleDetail,
  useGetKBArticleVersions,
  invalidateKBArticleVersions,
  usePostKBArticle,
  usePatchKBArticle,
  usePublishKBArticle,
  // Bots
  useGetBots,
  invalidateBots,
  useGetBotDetail,
  useGetBotVersions,
  invalidateBotVersions,
  usePostBot,
  usePatchBot,
  // Channels
  useGetChannels,
  invalidateChannels,
  useGetChannelDetail,
  usePostChannel,
  usePatchChannel,
  // Escalation Teams
  useGetEscalationTeams,
  // Disposition Properties
  useGetDispositionProperties,
  // Tools
  useGetTools,
  usePostToolTest,
  // Test Chat
  usePostTestChatMessage,
  usePostTicket,
};
